(() => {
  // Shopify renders this message in an open shadow root with its own font.
  // Keep this override limited to the installments paragraph, not branded buttons.
  const applyInstallmentsTypography = (container) => {
    const roots = new Map();
    const marker = 'data-inkhaven-installments-font';
    let active = true;
    let retry = null;
    let attempts = 0;
    const ensureStyle = (root) => {
      if (root.querySelector(`style[${marker}]`)) return;
      const style = document.createElement('style');
      style.setAttribute(marker, '');
      style.textContent = `#shopify-installments {
        font-family: var(--font-body--family, inherit) !important;
        font-style: var(--font-body--style, normal) !important;
        font-weight: var(--font-body--weight, 400) !important;
      }`;
      root.append(style);
    };
    const sync = () => {
      if (!active) return;
      const hosts = [...container.querySelectorAll('shopify-payment-terms')];
      roots.forEach((entry, host) => {
        if (!hosts.includes(host) || host.shadowRoot !== entry.root) {
          entry.observer.disconnect();
          roots.delete(host);
        }
      });
      let pending = false;
      hosts.forEach((host) => {
        const root = host.shadowRoot;
        if (!root) { pending = true; return; }
        if (roots.has(host)) return;
        ensureStyle(root);
        const observer = new MutationObserver(() => ensureStyle(root));
        observer.observe(root, { childList: true, subtree: true });
        roots.set(host, { root, observer });
      });
      // Normally whenDefined is sufficient. Bound retries for delayed attachment;
      // a closed or changed Shopify component must not keep polling indefinitely.
      if (pending && retry === null && attempts < 20) {
        retry = setTimeout(() => { retry = null; attempts += 1; sync(); }, 250);
      }
    };
    const observer = new MutationObserver(() => { attempts = 0; sync(); });
    observer.observe(container, { childList: true, subtree: true });
    customElements.whenDefined('shopify-payment-terms').then(sync);
    sync();
    return () => {
      active = false;
      clearTimeout(retry);
      observer.disconnect();
      roots.forEach(({ observer }) => observer.disconnect());
      roots.clear();
    };
  };

  class ProductBuyButtons extends HTMLElement {
    connectedCallback() {
      this.stopInstallmentsTypography?.();
      this.stopInstallmentsTypography = applyInstallmentsTypography(this);
      this.variantInput = this.querySelector('[data-product-variant-id]');
      this.quantityInput = this.querySelector('[data-product-quantity]');
      this.addButton = this.querySelector('[data-add-to-cart-button]');
      this.rsvpCheckoutButton = this.querySelector('[data-rsvp-checkout]');
      this.bookshopButton = this.querySelector('[data-bookshop-button]');
      this.dynamicCheckoutButtons = this.querySelector('[data-dynamic-checkout-buttons]');
      this.giftCardRecipient = this.querySelector('[data-gift-card-recipient]');
      this.giftCardRecipientToggle = this.querySelector('[data-gift-card-recipient-toggle]');
      this.giftCardRecipientFields = this.querySelector('[data-gift-card-recipient-fields]');
      this.giftCardRecipientInputs = Array.from(this.querySelectorAll('[data-gift-card-recipient-input]'));
      this.giftCardRecipientEmail = this.querySelector('[data-gift-card-recipient-email]');
      this.giftCardRecipientMessage = this.querySelector('[data-gift-card-recipient-message]');
      this.giftCardRecipientMessageCount = this.querySelector('[data-gift-card-recipient-message-count]');
      this.giftCardRecipientOffset = this.querySelector('[data-gift-card-recipient-offset]');
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onQuantityChange = this.onQuantityChange.bind(this);
      this.onGiftCardRecipientToggle = this.onGiftCardRecipientToggle.bind(this);
      this.onGiftCardMessageInput = this.onGiftCardMessageInput.bind(this);
      this.selectedVariantId = this.variantInput && this.variantInput.value;
      this.selectedVariantIsFreeEventTicket = this.dataset.selectedFreeEventTicket === 'true';

      document.addEventListener('product:variant-change', this.onVariantChange);
      document.addEventListener('product:quantity-change', this.onQuantityChange);

      if (this.giftCardRecipientToggle) {
        this.giftCardRecipientToggle.addEventListener('change', this.onGiftCardRecipientToggle);
        this.syncGiftCardRecipientFields();
      }

      if (this.giftCardRecipientMessage) {
        this.giftCardRecipientMessage.addEventListener('input', this.onGiftCardMessageInput);
        this.updateGiftCardMessageCount();
      }

      this.updateRsvpCheckoutLink();
    }

    disconnectedCallback() {
      this.stopInstallmentsTypography?.();
      document.removeEventListener('product:variant-change', this.onVariantChange);
      document.removeEventListener('product:quantity-change', this.onQuantityChange);

      if (this.giftCardRecipientToggle) {
        this.giftCardRecipientToggle.removeEventListener('change', this.onGiftCardRecipientToggle);
      }

      if (this.giftCardRecipientMessage) {
        this.giftCardRecipientMessage.removeEventListener('input', this.onGiftCardMessageInput);
      }
    }

    onVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      const eventScope = event.detail && event.detail.scope;

      if (!variant || !this.variantInput || !this.addButton) return;
      if ((eventScope || this.scope) && eventScope !== this.scope) return;

      this.variantInput.value = variant.id;
      this.selectedVariantId = variant.id;
      this.selectedVariantIsFreeEventTicket = Boolean(variant.is_free_event_ticket);
      this.variantInput.disabled = !variant.available;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.quantityInput.disabled = !variant.available;

      if (!variant.available) {
        this.quantityInput.value = '0';
      }

      const showBookshopButton =
        variant.available === false &&
        Boolean(variant.bookshop_oos_eligible) &&
        Boolean(variant.bookshop_url);

      this.addButton.hidden = showBookshopButton;
      this.addButton.disabled = !variant.available;
      this.addButton.textContent = variant.available
        ? this.addButton.dataset.addText
        : this.addButton.dataset.soldOutText;
      this.addButton.classList.toggle('button--primary', !this.selectedVariantIsFreeEventTicket);
      this.addButton.classList.toggle('button--secondary', this.selectedVariantIsFreeEventTicket);

      if (this.rsvpCheckoutButton) {
        this.rsvpCheckoutButton.hidden = !variant.available || !this.selectedVariantIsFreeEventTicket;
        this.updateRsvpCheckoutLink();
      }

      if (this.bookshopButton) {
        if (variant.bookshop_url) {
          this.bookshopButton.href = variant.bookshop_url;
        }

        this.bookshopButton.hidden = !showBookshopButton;
      }

      if (this.dynamicCheckoutButtons) {
        this.dynamicCheckoutButtons.hidden = !variant.available || this.selectedVariantIsFreeEventTicket;
      }
    }

    onQuantityChange(event) {
      const quantity = event.detail && event.detail.quantity;
      const eventScope = event.detail && event.detail.scope;

      if (!this.quantityInput || quantity === undefined) return;
      if ((eventScope || this.scope) && eventScope !== this.scope) return;

      this.quantityInput.value = String(quantity);
      this.updateRsvpCheckoutLink();
    }

    onGiftCardRecipientToggle() {
      this.syncGiftCardRecipientFields();
    }

    onGiftCardMessageInput() {
      this.updateGiftCardMessageCount();
    }

    syncGiftCardRecipientFields() {
      if (!this.giftCardRecipientToggle || !this.giftCardRecipientFields) return;

      const enabled = this.giftCardRecipientToggle.checked;

      this.giftCardRecipientFields.hidden = !enabled;

      this.giftCardRecipientInputs.forEach((input) => {
        input.disabled = !enabled;
      });

      if (this.giftCardRecipientEmail) {
        this.giftCardRecipientEmail.required = enabled;
      }

      if (this.giftCardRecipientOffset) {
        this.giftCardRecipientOffset.disabled = !enabled;
        this.giftCardRecipientOffset.value = enabled ? String(new Date().getTimezoneOffset()) : '';
      }
    }

    updateGiftCardMessageCount() {
      if (!this.giftCardRecipientMessage || !this.giftCardRecipientMessageCount) return;

      this.giftCardRecipientMessageCount.textContent = String(this.giftCardRecipientMessage.value.length);
    }

    updateRsvpCheckoutLink() {
      if (!this.rsvpCheckoutButton || !this.dataset.rsvpCheckoutBase || !this.selectedVariantId) return;

      const quantity = Math.max(Number.parseInt(this.quantityInput && this.quantityInput.value, 10) || 1, 1);
      this.rsvpCheckoutButton.href = `${this.dataset.rsvpCheckoutBase}${encodeURIComponent(this.selectedVariantId)}:${quantity}`;
    }

    get scope() {
      return this.dataset.productUiScope || this.closest('.product-layout, .featured-product-showcase, [data-search-product-drawer]') || '';
    }
  }

  if (!customElements.get('product-buy-buttons')) {
    customElements.define('product-buy-buttons', ProductBuyButtons);
  }
})();
